import {
	getMappedCollectionEntity,
	getRuntimeBrandKey,
	getRuntimeCollectionEntity,
	type CollectionEntity,
} from '#app/utils/pz/brand/routeEntityRuntime'

export type { CollectionEntity } from '#app/utils/pz/brand/routeEntityRuntime'

const DEFAULT_COLLECTION_ENTITY: CollectionEntity = 'collection'

const COLLECTION_ENTITY_PATH_OVERRIDES: Record<
	CollectionEntity,
	{
		dashboardBase: string
		marketingBase: string
	}
> = {
	collection: {
		dashboardBase: '/learn/collections',
		marketingBase: '/collections',
	},
	roadmap: {
		dashboardBase: '/learn/roadmaps',
		marketingBase: '/roadmaps',
	},
	program: {
		dashboardBase: '/learn/programs',
		marketingBase: '/programs',
	},
}

export function getCollectionEntity(
	brandKey?: string | null,
): CollectionEntity {
	return (
		getRuntimeCollectionEntity() ??
		getMappedCollectionEntity(brandKey ?? getRuntimeBrandKey()) ??
		DEFAULT_COLLECTION_ENTITY
	)
}

function getCollectionEntityConfig(entity?: CollectionEntity) {
	const target = entity ?? getCollectionEntity()
	return (
		COLLECTION_ENTITY_PATH_OVERRIDES[target] ??
		COLLECTION_ENTITY_PATH_OVERRIDES[DEFAULT_COLLECTION_ENTITY]
	)
}

export type CollectionEntityArea = 'dashboard' | 'marketing'

function getPathSegment(basePath: string, prefix: string) {
	const trimmed = basePath.startsWith(prefix)
		? basePath.slice(prefix.length)
		: basePath
	return trimmed.replace(/^\/+/, '').replace(/\/+$/, '')
}

export function getCollectionEntitySegment(
	area: CollectionEntityArea,
	collectionEntity?: CollectionEntity,
): string {
	const config = getCollectionEntityConfig(collectionEntity)
	if (area === 'dashboard') {
		return getPathSegment(config.dashboardBase, '/learn/')
	}
	return getPathSegment(config.marketingBase, '/')
}

export function assertCollectionEntitySegment(
	area: CollectionEntityArea,
	segment: string | undefined,
	collectionEntity?: CollectionEntity,
) {
	const expected = getCollectionEntitySegment(area, collectionEntity)
	if (!segment || segment !== expected) {
		throw new Response('Not Found', { status: 404 })
	}
}

export function applyCollectionEntityToPath(
	path: string,
	collectionEntity?: CollectionEntity,
): string {
	const baseConfig = getCollectionEntityConfig(DEFAULT_COLLECTION_ENTITY)
	const targetConfig = getCollectionEntityConfig(collectionEntity)
	if (targetConfig === baseConfig) return path

	return path
		.replace(
			new RegExp(`^${baseConfig.dashboardBase}(\\/|$)`),
			`${targetConfig.dashboardBase}$1`,
		)
		.replace(
			new RegExp(`^${baseConfig.marketingBase}(\\/|$)`),
			`${targetConfig.marketingBase}$1`,
		)
}

export function normalizeCollectionMarketingPath(
	path: string,
	collectionEntity?: CollectionEntity,
): string {
	const targetConfig = getCollectionEntityConfig(collectionEntity)

	for (const sourceConfig of Object.values(COLLECTION_ENTITY_PATH_OVERRIDES)) {
		const dashboardPattern = new RegExp(`^${sourceConfig.dashboardBase}(\\/|$)`)
		if (dashboardPattern.test(path)) {
			return path.replace(dashboardPattern, `${targetConfig.marketingBase}$1`)
		}

		const marketingPattern = new RegExp(`^${sourceConfig.marketingBase}(\\/|$)`)
		if (marketingPattern.test(path)) {
			return path.replace(marketingPattern, `${targetConfig.marketingBase}$1`)
		}
	}

	return path
}
